import { FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Button, ChatIconSmall, ZendeskChatLoadingOverlay } from "../components";
import { openZendeskChat, registerOnZendeskChatOpen } from "../scripts";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils";

type ZContext = {
  openZendesk: (from: string) => void;
};

const ZendeskContext = React.createContext<ZContext | null>(null);

export const useZendeskContext = () => useContext(ZendeskContext) as ZContext;

const StyledChatIcon = styled(ChatIconSmall)`
  cursor: pointer;
  position: relative;
  top: -2px;
`;

export const ZendeskProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { apiClient } = useApiContext();

  const track = useTrack();

  const isAuthenticated = useSelector((state: AppState) => state.app.coeffUserId !== "");

  const email = useSelector((state: AppState) => state.app.email);
  const name = useSelector((state: AppState) => state.app.name);

  const { value: zendeskMessengerEnabled } = useFeatureGate(FeatureFlag.EnableZendeskMessenger);

  const [unreadSupportChatMessages, setUnreadSupportChatMessages] = useState<number>(0);

  const [isZendeskLoaded, setIsZendeskLoaded] = useState<boolean>(false);

  // Mark as loaded once the chat has been opened at least once, so the unread badge can show.
  useEffect(
    () =>
      registerOnZendeskChatOpen(() => {
        setIsZendeskLoaded(true);
        setUnreadSupportChatMessages(0);
      }),
    []
  );

  const openZendesk = (from: string) => {
    openZendeskChat(apiClient, {
      zendeskMessengerEnabled,
      user: { email, name },
      // Callback is captured once (guarded by unreadMessageListenerRegistered) so it must only
      // close over stable references like React state setters.
      onUnreadMessages: (count: number) => {
        console.log(`It seems you have ${count} unread messages!`);
        setUnreadSupportChatMessages(count);
      },
    });

    // Always track when the user clicks to open or focus support chat (including re-focus in new-window mode).
    track("help_center_clicked", {
      event_from: from,
    });
  };

  const initLoadCompleted = isZendeskLoaded && isAuthenticated;

  return (
    <ZendeskContext.Provider value={{ openZendesk }}>
      <>
        {children}
        <ZendeskChatLoadingOverlay />
        {initLoadCompleted && unreadSupportChatMessages > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: "24px",
              right: "24px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button type="link" size="small" onClick={() => openZendesk("webapp")}>
              <StyledChatIcon />
            </Button>
          </div>
        )}
      </>
    </ZendeskContext.Provider>
  );
};
